import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Crosshair, Play, Pause, Square, RefreshCw, Plus, Search, Filter,
  Shield, Target, Zap, AlertTriangle, CheckCircle, XCircle, Clock,
  Brain, Cpu, Activity, TrendingUp, TrendingDown, BarChart3, PieChart, ArrowRight,
  ChevronDown, ChevronUp, ChevronRight, ExternalLink, Download, Upload,
  Settings, Calendar, Users, Lock, Unlock, Eye, EyeOff, Copy, Trash2,
  FileText, Database, Server, Globe, Network, Bug, Skull, ShieldAlert,
  Layers, GitBranch, Send, Loader2, Info, HelpCircle, Sparkles, Bell,
  GripVertical, Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// MITRE ATT&CK Tactics
const MITRE_TACTICS = [
  { id: 'TA0043', name: 'Reconnaissance', color: 'slate' },
  { id: 'TA0042', name: 'Resource Development', color: 'gray' },
  { id: 'TA0001', name: 'Initial Access', color: 'red' },
  { id: 'TA0002', name: 'Execution', color: 'orange' },
  { id: 'TA0003', name: 'Persistence', color: 'amber' },
  { id: 'TA0004', name: 'Privilege Escalation', color: 'yellow' },
  { id: 'TA0005', name: 'Defense Evasion', color: 'lime' },
  { id: 'TA0006', name: 'Credential Access', color: 'green' },
  { id: 'TA0007', name: 'Discovery', color: 'emerald' },
  { id: 'TA0008', name: 'Lateral Movement', color: 'teal' },
  { id: 'TA0009', name: 'Collection', color: 'cyan' },
  { id: 'TA0011', name: 'Command & Control', color: 'sky' },
  { id: 'TA0010', name: 'Exfiltration', color: 'blue' },
  { id: 'TA0040', name: 'Impact', color: 'violet' },
];

// Sample attack techniques for each tactic
const ATTACK_TECHNIQUES = {
  'TA0001': [
    { id: 'T1190', name: 'Exploit Public-Facing Application', severity: 'critical' },
    { id: 'T1133', name: 'External Remote Services', severity: 'high' },
    { id: 'T1566', name: 'Phishing', severity: 'high' },
    { id: 'T1078', name: 'Valid Accounts', severity: 'critical' },
    { id: 'T1189', name: 'Drive-by Compromise', severity: 'high' },
  ],
  'TA0002': [
    { id: 'T1059', name: 'Command and Scripting Interpreter', severity: 'high' },
    { id: 'T1203', name: 'Exploitation for Client Execution', severity: 'critical' },
    { id: 'T1047', name: 'Windows Management Instrumentation', severity: 'medium' },
    { id: 'T1053', name: 'Scheduled Task/Job', severity: 'medium' },
  ],
  'TA0003': [
    { id: 'T1547', name: 'Boot or Logon Autostart Execution', severity: 'high' },
    { id: 'T1543', name: 'Create or Modify System Process', severity: 'high' },
    { id: 'T1136', name: 'Create Account', severity: 'medium' },
  ],
  'TA0004': [
    { id: 'T1548', name: 'Abuse Elevation Control Mechanism', severity: 'high' },
    { id: 'T1134', name: 'Access Token Manipulation', severity: 'high' },
    { id: 'T1068', name: 'Exploitation for Privilege Escalation', severity: 'critical' },
  ],
  'TA0005': [
    { id: 'T1562', name: 'Impair Defenses', severity: 'critical' },
    { id: 'T1070', name: 'Indicator Removal', severity: 'high' },
    { id: 'T1036', name: 'Masquerading', severity: 'medium' },
  ],
  'TA0006': [
    { id: 'T1110', name: 'Brute Force', severity: 'high' },
    { id: 'T1555', name: 'Credentials from Password Stores', severity: 'critical' },
    { id: 'T1003', name: 'OS Credential Dumping', severity: 'critical' },
  ],
  'TA0007': [
    { id: 'T1087', name: 'Account Discovery', severity: 'low' },
    { id: 'T1482', name: 'Domain Trust Discovery', severity: 'medium' },
    { id: 'T1083', name: 'File and Directory Discovery', severity: 'low' },
  ],
  'TA0008': [
    { id: 'T1021', name: 'Remote Services', severity: 'high' },
    { id: 'T1570', name: 'Lateral Tool Transfer', severity: 'high' },
    { id: 'T1550', name: 'Use Alternate Authentication Material', severity: 'critical' },
  ],
  'TA0011': [
    { id: 'T1071', name: 'Application Layer Protocol', severity: 'high' },
    { id: 'T1095', name: 'Non-Application Layer Protocol', severity: 'high' },
    { id: 'T1572', name: 'Protocol Tunneling', severity: 'high' },
  ],
  'TA0040': [
    { id: 'T1486', name: 'Data Encrypted for Impact', severity: 'critical' },
    { id: 'T1489', name: 'Service Stop', severity: 'high' },
    { id: 'T1490', name: 'Inhibit System Recovery', severity: 'critical' },
  ],
};

export default function BASEngine() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [campaigns, setCampaigns] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningSimulation, setRunningSimulation] = useState(null);
  const [selectedTactic, setSelectedTactic] = useState(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingCampaign, setGeneratingCampaign] = useState(false);
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeSimulations: 0,
    techniquesTestedToday: 0,
    detectionRate: 0,
    coverageScore: 0
  });

  // NEW: State for Attack Library selection
  const [selectedTechniques, setSelectedTechniques] = useState([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryTacticFilter, setLibraryTacticFilter] = useState('all');
  const [showCreateCustom, setShowCreateCustom] = useState(false);
  const [customCampaignName, setCustomCampaignName] = useState('');
  const [customCampaignDesc, setCustomCampaignDesc] = useState('');
  
  // NEW: State for Prebuilt Scenarios
  const [scenarios, setScenarios] = useState([]);
  const [scenarioFilter, setScenarioFilter] = useState('all');
  const [deployingScenario, setDeployingScenario] = useState(null);
  
  // NEW: State for Campaign Comparison & Trends
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  
  // NEW: State for Agentic Workflow Real-time Updates
  const [agenticWorkflow, setAgenticWorkflow] = useState(null);
  const [workflowUpdates, setWorkflowUpdates] = useState([]);
  const [agenticLogs, setAgenticLogs] = useState([]);
  const [runningWorkflow, setRunningWorkflow] = useState(false);
  const [selectedCampaignForAgentic, setSelectedCampaignForAgentic] = useState(null);
  
  // NEW: State for Simulation Results
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [showSimulationResults, setShowSimulationResults] = useState(false);
  
  // NEW: State for AI Analysis
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAiAnalysis, setLoadingAiAnalysis] = useState(false);
  
  // NEW: State for MITRE Navigator
  const [mitreCoverage, setMitreCoverage] = useState(null);
  const [loadingMitreCoverage, setLoadingMitreCoverage] = useState(false);

  // NEW: State for Campaign Configuration Modal
  const [showCampaignConfig, setShowCampaignConfig] = useState(false);
  const [configCampaign, setConfigCampaign] = useState(null);
  const [campaignConfig, setCampaignConfig] = useState({
    target: 'https://target.example.com',
    stealthMode: true,
    maxConcurrentTechniques: 3,
    delayBetweenTechniques: 2,
    notifyOnCompletion: true,
    selectedTechniques: []
  });

  // NEW: State for Schedule Configuration Modal
  const [showScheduleConfig, setShowScheduleConfig] = useState(false);
  const [configSchedule, setConfigSchedule] = useState(null);
  const [scheduleConfig, setScheduleConfig] = useState({
    scheduleType: 'weekly',
    cronExpression: '0 2 * * 0',
    alertThreshold: 70,
    autoCompare: true,
    notifyOnDrop: true,
    notificationEmail: ''
  });
  const [schedules, setSchedules] = useState([]);

  // NEW: State for Custom MITRE Techniques
  const [showCustomTechnique, setShowCustomTechnique] = useState(false);
  const [customTechniques, setCustomTechniques] = useState([]);
  const [newTechnique, setNewTechnique] = useState({
    id: 'T9001',
    name: '',
    description: '',
    tactic: 'TA0001',
    severity: 'high',
    detection_methods: '',
    mitigations: ''
  });

  // NEW: State for Scenario Builder (Drag & Drop)
  const [showScenarioBuilder, setShowScenarioBuilder] = useState(false);
  const [builderScenario, setBuilderScenario] = useState({
    name: '',
    description: '',
    phases: [{ name: 'Phase 1', techniques: [] }]
  });
  const [draggedTechnique, setDraggedTechnique] = useState(null);

  // Fetch BAS data
  const fetchBASData = useCallback(async () => {
    setLoading(true);
    try {
      const [campaignsRes, simulationsRes, statsRes, scenariosRes] = await Promise.all([
        api.get('/bas/campaigns'),
        api.get('/bas/simulations?limit=10'),
        api.get('/bas/stats'),
        api.get('/bas/scenarios')
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setSimulations(simulationsRes.data.simulations || []);
      setStats(prev => statsRes.data || prev);
      setScenarios(scenariosRes.data.scenarios || []);
    } catch (error) {
      console.error('Failed to fetch BAS data:', error);
      // Don't use sample data - show empty state instead
      setCampaigns([]);
      setSimulations([]);
      setScenarios([]);
      toast.error('Failed to fetch BAS data', { description: 'Please check your connection' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch trends data
  const fetchTrendsData = useCallback(async () => {
    setLoadingTrends(true);
    try {
      const response = await api.get('/bas/trends?days=30');
      setTrendsData(response.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
      // Don't use sample data - show empty state
      setTrendsData({ timeline: [], trend: { direction: 'stable', change_percentage: 0, current_avg_detection: 0 } });
    } finally {
      setLoadingTrends(false);
    }
  }, []);

  useEffect(() => {
    fetchBASData();
  }, [fetchBASData]);

  // Fetch trends when switching to analytics tab
  useEffect(() => {
    if (activeTab === 'analytics' && !trendsData) {
      fetchTrendsData();
    }
  }, [activeTab, trendsData, fetchTrendsData]);

  // WebSocket connection for real-time agentic workflow updates
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || activeTab !== 'agentic') return;
    
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws?token=${token}`);
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'bas_workflow_update') {
          setWorkflowUpdates(prev => [...prev, {
            ...data,
            receivedAt: new Date().toISOString()
          }]);
          
          // Update workflow state based on stage
          setAgenticWorkflow(prev => ({
            ...prev,
            currentStage: data.stage,
            stageStatus: data.status,
            stageData: data.data
          }));
          
          if (data.stage === 'workflow' && data.status === 'completed') {
            setRunningWorkflow(false);
            toast.success('Agentic workflow completed!', {
              description: `Detection rate: ${data.data?.detection_rate}%`
            });
            fetchBASData();
          }
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };
    
    return () => ws.close();
  }, [activeTab, fetchBASData]);

  // Start simulation
  const startSimulation = async (campaignId) => {
    try {
      setRunningSimulation(campaignId);
      const response = await api.post(`/bas/campaigns/${campaignId}/run`);
      toast.success('Simulation started', {
        description: 'Attack simulation is now running'
      });
      fetchBASData();
    } catch (error) {
      toast.error('Failed to start simulation');
    } finally {
      setRunningSimulation(null);
    }
  };

  // Generate AI campaign
  const generateAICampaign = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a campaign description');
      return;
    }
    
    setGeneratingCampaign(true);
    try {
      const response = await api.post('/bas/campaigns/generate', {
        prompt: aiPrompt,
        include_mitre_mapping: true
      });
      
      toast.success('Campaign generated!', {
        description: 'AI has created your attack campaign'
      });
      
      setShowAIGenerator(false);
      setAiPrompt('');
      fetchBASData();
    } catch (error) {
      toast.error('Failed to generate campaign', {
        description: 'AI generation is not available'
      });
    } finally {
      setGeneratingCampaign(false);
    }
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'running':
        return <Badge className="bg-green-500 text-white animate-pulse">Running</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500 text-white">Completed</Badge>;
      case 'scheduled':
        return <Badge className="bg-yellow-500 text-black">Scheduled</Badge>;
      case 'active':
        return <Badge className="bg-emerald-500 text-white">Active</Badge>;
      case 'failed':
        return <Badge className="bg-red-500 text-white">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // NEW: Toggle technique selection
  const toggleTechniqueSelection = (techId) => {
    setSelectedTechniques(prev => 
      prev.includes(techId) 
        ? prev.filter(id => id !== techId)
        : [...prev, techId]
    );
  };

  // NEW: Create campaign from selected techniques
  const createCustomCampaign = async () => {
    if (selectedTechniques.length === 0) {
      toast.error('Select at least one technique');
      return;
    }
    if (!customCampaignName.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    
    try {
      await api.post('/bas/campaigns/from-techniques', {
        name: customCampaignName,
        description: customCampaignDesc || `Custom campaign with ${selectedTechniques.length} techniques`,
        techniques: selectedTechniques
      });
      
      toast.success('Custom campaign created!', {
        description: `${selectedTechniques.length} techniques selected`
      });
      
      setShowCreateCustom(false);
      setSelectedTechniques([]);
      setCustomCampaignName('');
      setCustomCampaignDesc('');
      fetchBASData();
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  // NEW: Deploy prebuilt scenario
  const deployScenario = async (scenarioId) => {
    setDeployingScenario(scenarioId);
    try {
      const response = await api.post(`/bas/scenarios/${scenarioId}/deploy`);
      toast.success('Scenario deployed!', {
        description: response.data.campaign?.name
      });
      fetchBASData();
      setActiveTab('campaigns');
    } catch (error) {
      toast.error('Failed to deploy scenario');
    } finally {
      setDeployingScenario(null);
    }
  };

  // NEW: Compare campaigns
  const compareCampaigns = async () => {
    if (selectedForCompare.length < 2) {
      toast.error('Select at least 2 campaigns to compare');
      return;
    }
    
    try {
      const response = await api.get('/bas/campaigns/compare', {
        params: { campaign_ids: selectedForCompare.join(',') }
      });
      setComparisonData(response.data);
      setActiveTab('analytics');
    } catch (error) {
      toast.error('Failed to compare campaigns');
    }
  };

  // NEW: Toggle campaign selection for comparison
  const toggleCompareSelection = (campaignId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(campaignId)) {
        return prev.filter(id => id !== campaignId);
      }
      if (prev.length >= 5) {
        toast.error('Maximum 5 campaigns can be compared');
        return prev;
      }
      return [...prev, campaignId];
    });
  };

  // NEW: Start agentic workflow
  const startAgenticWorkflow = async (campaignId) => {
    const targetCampaignId = campaignId || selectedCampaignForAgentic || (campaigns.length > 0 ? campaigns[0].id : null);
    
    if (!targetCampaignId) {
      toast.error('No campaign available', {
        description: 'Please create a campaign first or select one'
      });
      return;
    }
    
    setRunningWorkflow(true);
    setWorkflowUpdates([]);
    setAgenticWorkflow({ currentStage: 'planner', stageStatus: 'pending' });
    
    try {
      const response = await api.post('/bas/agentic/run', {
        campaign_id: targetCampaignId,
        target: 'https://target.example.com',
        options: { stealth_mode: true }
      });
      
      setAgenticWorkflow(prev => ({
        ...prev,
        workflowId: response.data.workflow_id
      }));
      
      toast.success('Agentic workflow started', {
        description: 'Real-time updates will appear below'
      });
      
      // Poll for workflow status since WebSocket might not be connected
      pollWorkflowStatus(response.data.workflow_id);
    } catch (error) {
      console.error('Agentic workflow error:', error);
      setRunningWorkflow(false);
      toast.error('Failed to start agentic workflow', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  // Poll workflow status for updates
  const pollWorkflowStatus = async (workflowId) => {
    let attempts = 0;
    const maxAttempts = 30; // Poll for max 60 seconds
    
    const poll = async () => {
      try {
        const response = await api.get(`/bas/agentic/${workflowId}`);
        const workflow = response.data;
        
        // Update workflow state
        setAgenticWorkflow(prev => ({
          ...prev,
          ...workflow,
          currentStage: workflow.current_stage
        }));
        
        // Add updates based on stage status
        if (workflow.stages) {
          const newUpdates = [];
          Object.entries(workflow.stages).forEach(([stage, data]) => {
            if (data.status === 'completed' || data.status === 'running') {
              newUpdates.push({
                stage,
                status: data.status,
                data: data.output || {},
                receivedAt: data.completed_at || data.started_at || new Date().toISOString()
              });
            }
          });
          setWorkflowUpdates(newUpdates);
        }
        
        if (workflow.status === 'completed') {
          setRunningWorkflow(false);
          toast.success('Agentic workflow completed!', {
            description: `Detection rate: ${workflow.results?.validation?.summary?.detection_rate || 'N/A'}%`
          });
          fetchBASData();
          return;
        }
        
        if (workflow.status === 'failed') {
          setRunningWorkflow(false);
          toast.error('Workflow failed');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setRunningWorkflow(false);
        }
      } catch (error) {
        console.error('Poll error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      }
    };
    
    setTimeout(poll, 2000);
  };

  // View simulation results
  const viewSimulationResults = (simulation) => {
    setSelectedSimulation(simulation);
    setAiAnalysis(null); // Reset AI analysis when viewing new simulation
    setShowSimulationResults(true);
  };

  // Generate recommendations based on simulation results
  const generateRecommendations = (sim) => {
    const recommendations = [];
    const detectionRate = sim.techniques_detected / Math.max(sim.techniques_total, 1) * 100;
    const blockRate = sim.techniques_blocked / Math.max(sim.techniques_total, 1) * 100;
    
    if (detectionRate < 50) {
      recommendations.push({
        severity: 'critical',
        title: 'Low Detection Rate',
        description: `Only ${detectionRate.toFixed(0)}% of techniques were detected. Consider improving SIEM rules and EDR configurations.`,
        action: 'Review and update detection rules for the following tactics'
      });
    } else if (detectionRate < 80) {
      recommendations.push({
        severity: 'high',
        title: 'Detection Gaps Identified',
        description: `Detection rate of ${detectionRate.toFixed(0)}% indicates gaps in coverage.`,
        action: 'Enhance monitoring for techniques that evaded detection'
      });
    }
    
    if (blockRate < 30) {
      recommendations.push({
        severity: 'critical',
        title: 'Low Block Rate',
        description: `Only ${blockRate.toFixed(0)}% of detected techniques were blocked. Security controls may need strengthening.`,
        action: 'Review and harden endpoint protection policies'
      });
    }
    
    const evadedCount = sim.techniques_total - sim.techniques_detected;
    if (evadedCount > 0) {
      recommendations.push({
        severity: evadedCount > 3 ? 'high' : 'medium',
        title: `${evadedCount} Techniques Evaded Detection`,
        description: 'These techniques bypassed current security controls.',
        action: 'Create custom detection rules for evaded techniques'
      });
    }
    
    // Always add improvement suggestions
    recommendations.push({
      severity: 'info',
      title: 'Continuous Improvement',
      description: 'Schedule regular simulations to track detection improvements over time.',
      action: 'Set up automated weekly or monthly BAS campaigns'
    });
    
    return recommendations;
  };

  // Export simulation results to PDF
  const exportSimulationPDF = (sim) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(220, 38, 38); // Red color for Defendly
    doc.text('Defendly AI', 14, yPos);
    yPos += 10;
    
    // Report Title
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('BAS Simulation Results Report', 14, yPos);
    yPos += 8;
    
    // Campaign Name
    doc.setFontSize(12);
    doc.text(sim.campaign_name || 'Unknown Campaign', 14, yPos);
    yPos += 6;
    
    // Generated Date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
    doc.text(`Target: ${sim.target || 'N/A'}`, 100, yPos);
    yPos += 15;
    
    // Summary Stats
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Summary', 14, yPos);
    yPos += 10;
    
    const detectionRate = ((sim.techniques_detected || 0) / Math.max(sim.techniques_total, 1) * 100).toFixed(1);
    const blockRate = ((sim.techniques_blocked || 0) / Math.max(sim.techniques_total, 1) * 100).toFixed(1);
    const evadedCount = (sim.techniques_total || 0) - (sim.techniques_detected || 0);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Total Techniques Tested', String(sim.techniques_total || 0)],
        ['Techniques Detected', String(sim.techniques_detected || 0)],
        ['Techniques Blocked', String(sim.techniques_blocked || 0)],
        ['Techniques Evaded', String(evadedCount)],
        ['Detection Rate', `${detectionRate}%`],
        ['Block Rate', `${blockRate}%`],
        ['Status', sim.status || 'completed'],
        ['Completed At', sim.completed_at ? new Date(sim.completed_at).toLocaleString() : 'N/A']
      ],
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
    
    // Recommendations
    const recommendations = generateRecommendations(sim);
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Recommendations', 14, yPos);
    yPos += 10;
    
    recommendations.forEach((rec, idx) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      
      const severityColors = {
        'critical': [220, 38, 38],
        'high': [234, 88, 12],
        'medium': [234, 179, 8],
        'info': [59, 130, 246]
      };
      
      doc.setFontSize(11);
      const color = severityColors[rec.severity] || [100, 100, 100];
      doc.setTextColor(...color);
      doc.text(`[${rec.severity.toUpperCase()}] ${rec.title}`, 14, yPos);
      yPos += 6;
      
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      const descLines = doc.splitTextToSize(rec.description, pageWidth - 28);
      doc.text(descLines, 14, yPos);
      yPos += descLines.length * 5 + 3;
      
      doc.setTextColor(100, 100, 100);
      doc.text(`Action: ${rec.action}`, 14, yPos);
      yPos += 10;
    });
    
    // Suggested Detection Rules
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    yPos += 5;
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Suggested Detection Rules', 14, yPos);
    yPos += 10;
    
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text('SIEM Rule - Detect lateral movement attempts:', 14, yPos);
    yPos += 5;
    doc.setFont('courier', 'normal');
    doc.text('index=security sourcetype=windows:security EventCode=4624 Logon_Type=3', 14, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.text('EDR Policy - Block credential dumping:', 14, yPos);
    yPos += 5;
    doc.setFont('courier', 'normal');
    doc.text('process.name:("mimikatz.exe" OR "procdump.exe")', 14, yPos);
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text('CONFIDENTIAL - Defendly AI Security Report', pageWidth / 2, 290, { align: 'center' });
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, 290, { align: 'right' });
    }
    
    doc.save(`BAS_Simulation_${sim.campaign_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Report exported to PDF');
  };

  // Generate comprehensive BAS report (Executive, Technical, or Remediation)
  const generateComprehensiveReport = async (reportType) => {
    try {
      toast.info(`Generating ${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report...`);
      
      const response = await api.get(`/bas/reports/generate/${reportType}?format=html`, {
        responseType: 'text'
      });
      
      // Open report in new window for viewing/printing
      const reportWindow = window.open('', '_blank');
      reportWindow.document.write(response.data);
      reportWindow.document.close();
      
      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report opened in new tab. Use Ctrl+P to save as PDF.`);
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(`Failed to generate ${reportType} report`);
    }
  };

  // Fetch AI-powered analysis for simulation
  const fetchAiAnalysis = async (simulationId) => {
    setLoadingAiAnalysis(true);
    try {
      const response = await api.post(`/bas/simulations/${simulationId}/ai-analysis`);
      setAiAnalysis(response.data);
      toast.success('AI analysis generated');
    } catch (error) {
      console.error('AI analysis error:', error);
      toast.error('Failed to generate AI analysis');
    } finally {
      setLoadingAiAnalysis(false);
    }
  };

  // Fetch MITRE ATT&CK coverage data
  const fetchMitreCoverage = async () => {
    setLoadingMitreCoverage(true);
    try {
      const response = await api.get('/metrics/mitre-coverage?days=30');
      setMitreCoverage(response.data);
    } catch (error) {
      console.error('MITRE coverage error:', error);
      // Use empty state instead of sample data
      setMitreCoverage({
        summary: { total_techniques_tested: 0, total_simulations: 0, avg_detection_rate: 0 },
        tactic_coverage: {},
        coverage_gaps: []
      });
    } finally {
      setLoadingMitreCoverage(false);
    }
  };

  // Get coverage color for MITRE heatmap
  const getCoverageColor = (level, rate) => {
    if (level === 'none' || rate === 0) return isDarkMode ? 'bg-slate-800' : 'bg-slate-200';
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 60) return 'bg-yellow-500';
    if (rate >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Fetch MITRE coverage when switching to that tab
  useEffect(() => {
    if (activeTab === 'navigator' && !mitreCoverage) {
      fetchMitreCoverage();
    }
  }, [activeTab, mitreCoverage]);

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 text-green-500 border-green-500/30';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
      case 'advanced': return 'bg-red-500/20 text-red-500 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
    }
  };

  // Get category label
  const getCategoryLabel = (category) => {
    const labels = {
      'nation_state': 'Nation State',
      'cybercrime': 'Cybercrime',
      'social_engineering': 'Social Engineering',
      'insider_threat': 'Insider Threat',
      'cloud_attack': 'Cloud Attack',
      'supply_chain': 'Supply Chain'
    };
    return labels[category] || category;
  };

  // Filter techniques for library
  const getFilteredTechniques = () => {
    let allTechniques = [];
    Object.entries(ATTACK_TECHNIQUES).forEach(([tacticId, techniques]) => {
      const tactic = MITRE_TACTICS.find(t => t.id === tacticId);
      techniques.forEach(tech => {
        allTechniques.push({ ...tech, tacticId, tacticName: tactic?.name });
      });
    });
    
    if (libraryTacticFilter !== 'all') {
      allTechniques = allTechniques.filter(t => t.tacticId === libraryTacticFilter);
    }
    
    if (librarySearch) {
      const search = librarySearch.toLowerCase();
      allTechniques = allTechniques.filter(t => 
        t.name.toLowerCase().includes(search) || 
        t.id.toLowerCase().includes(search)
      );
    }
    
    return allTechniques;
  };

  // NEW: Open campaign configuration modal
  const openCampaignConfig = (campaign) => {
    setConfigCampaign(campaign);
    setCampaignConfig({
      target: campaign.target || 'https://target.example.com',
      stealthMode: true,
      maxConcurrentTechniques: 3,
      delayBetweenTechniques: 2,
      notifyOnCompletion: true,
      selectedTechniques: campaign.techniques || []
    });
    setShowCampaignConfig(true);
  };

  // NEW: Save campaign configuration and run
  const saveCampaignConfig = async () => {
    if (!configCampaign) return;
    
    try {
      // Update campaign config
      await api.patch(`/bas/campaigns/${configCampaign.id}`, {
        target: campaignConfig.target,
        config: {
          stealth_mode: campaignConfig.stealthMode,
          max_concurrent: campaignConfig.maxConcurrentTechniques,
          delay_seconds: campaignConfig.delayBetweenTechniques,
          notify_on_completion: campaignConfig.notifyOnCompletion
        }
      });
      
      toast.success('Campaign configured successfully');
      setShowCampaignConfig(false);
      fetchBASData();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  // NEW: Run configured campaign
  const runConfiguredCampaign = async () => {
    if (!configCampaign) return;
    
    try {
      await api.post(`/bas/campaigns/${configCampaign.id}/run`, {
        target: campaignConfig.target,
        options: {
          stealth_mode: campaignConfig.stealthMode,
          max_concurrent: campaignConfig.maxConcurrentTechniques,
          delay_seconds: campaignConfig.delayBetweenTechniques
        }
      });
      
      toast.success('Campaign started with configuration');
      setShowCampaignConfig(false);
      fetchBASData();
    } catch (error) {
      toast.error('Failed to start campaign');
    }
  };

  // NEW: Open schedule configuration modal
  const openScheduleConfig = (schedule) => {
    setConfigSchedule(schedule);
    setScheduleConfig({
      scheduleType: schedule.schedule_type || 'weekly',
      cronExpression: schedule.cron_expression || '0 2 * * 0',
      alertThreshold: schedule.alert_threshold || 70,
      autoCompare: schedule.auto_compare !== false,
      notifyOnDrop: schedule.notify_on_drop !== false,
      notificationEmail: schedule.notification_email || ''
    });
    setShowScheduleConfig(true);
  };

  // NEW: Save schedule configuration
  const saveScheduleConfig = async () => {
    if (!configSchedule) return;
    
    try {
      await api.patch(`/bas/schedules/${configSchedule.id}`, {
        schedule_type: scheduleConfig.scheduleType,
        cron_expression: scheduleConfig.cronExpression,
        alert_threshold: scheduleConfig.alertThreshold,
        auto_compare: scheduleConfig.autoCompare,
        notify_on_drop: scheduleConfig.notifyOnDrop,
        notification_email: scheduleConfig.notificationEmail
      });
      
      toast.success('Schedule configured successfully');
      setShowScheduleConfig(false);
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to save schedule');
    }
  };

  // NEW: Resume/restart paused schedule
  const resumeSchedule = async (scheduleId) => {
    try {
      await api.post(`/bas/schedules/${scheduleId}/resume`);
      toast.success('Schedule resumed');
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to resume schedule');
    }
  };

  // NEW: Pause schedule
  const pauseSchedule = async (scheduleId) => {
    try {
      await api.post(`/bas/schedules/${scheduleId}/pause`);
      toast.success('Schedule paused');
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to pause schedule');
    }
  };

  // NEW: Fetch schedules
  const fetchSchedules = async () => {
    try {
      const response = await api.get('/bas/schedules');
      setSchedules(response.data.schedules || []);
    } catch (error) {
      // Use empty state instead of sample data
      setSchedules([]);
    }
  };

  // NEW: Fetch custom techniques
  const fetchCustomTechniques = async () => {
    try {
      const response = await api.get('/bas/techniques/custom');
      setCustomTechniques(response.data.techniques || []);
    } catch (error) {
      setCustomTechniques([]);
    }
  };

  // NEW: Save custom technique
  const saveCustomTechnique = async () => {
    if (!newTechnique.name.trim()) {
      toast.error('Technique name is required');
      return;
    }
    
    try {
      await api.post('/bas/techniques/custom', newTechnique);
      toast.success('Custom technique created');
      setShowCustomTechnique(false);
      setNewTechnique({
        id: `T${9000 + customTechniques.length + 1}`,
        name: '',
        description: '',
        tactic: 'TA0001',
        severity: 'high',
        detection_methods: '',
        mitigations: ''
      });
      fetchCustomTechniques();
    } catch (error) {
      toast.error('Failed to create technique');
    }
  };

  // NEW: Delete custom technique
  const deleteCustomTechnique = async (techId) => {
    try {
      await api.delete(`/bas/techniques/custom/${techId}`);
      toast.success('Technique deleted');
      fetchCustomTechniques();
    } catch (error) {
      toast.error('Failed to delete technique');
    }
  };

  // NEW: Scenario Builder - Add phase
  const addBuilderPhase = () => {
    setBuilderScenario(prev => ({
      ...prev,
      phases: [...prev.phases, { name: `Phase ${prev.phases.length + 1}`, techniques: [] }]
    }));
  };

  // NEW: Scenario Builder - Remove phase
  const removeBuilderPhase = (phaseIndex) => {
    setBuilderScenario(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== phaseIndex)
    }));
  };

  // NEW: Scenario Builder - Handle drag start
  const handleDragStart = (e, technique) => {
    setDraggedTechnique(technique);
    e.dataTransfer.effectAllowed = 'move';
  };

  // NEW: Scenario Builder - Handle drop
  const handleDrop = (e, phaseIndex) => {
    e.preventDefault();
    if (!draggedTechnique) return;
    
    setBuilderScenario(prev => {
      const newPhases = [...prev.phases];
      if (!newPhases[phaseIndex].techniques.find(t => t.id === draggedTechnique.id)) {
        newPhases[phaseIndex].techniques.push(draggedTechnique);
      }
      return { ...prev, phases: newPhases };
    });
    setDraggedTechnique(null);
  };

  // NEW: Scenario Builder - Remove technique from phase
  const removeTechniqueFromPhase = (phaseIndex, techId) => {
    setBuilderScenario(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex].techniques = newPhases[phaseIndex].techniques.filter(t => t.id !== techId);
      return { ...prev, phases: newPhases };
    });
  };

  // NEW: Scenario Builder - Save scenario as campaign
  const saveBuilderScenario = async () => {
    if (!builderScenario.name.trim()) {
      toast.error('Scenario name is required');
      return;
    }
    
    const allTechniques = builderScenario.phases.flatMap(p => p.techniques.map(t => t.id));
    if (allTechniques.length === 0) {
      toast.error('Add at least one technique');
      return;
    }
    
    try {
      await api.post('/bas/scenarios/custom', {
        name: builderScenario.name,
        description: builderScenario.description,
        phases: builderScenario.phases.map(p => ({
          name: p.name,
          techniques: p.techniques.map(t => t.id)
        })),
        techniques: allTechniques
      });
      
      toast.success('Custom scenario created!');
      setShowScenarioBuilder(false);
      setBuilderScenario({ name: '', description: '', phases: [{ name: 'Phase 1', techniques: [] }] });
      fetchBASData();
    } catch (error) {
      toast.error('Failed to create scenario');
    }
  };

  // Fetch schedules and custom techniques on mount
  useEffect(() => {
    fetchSchedules();
    fetchCustomTechniques();
  }, []);

  return (
    <div className={`min-h-screen p-6 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-red-500/20 border border-red-500/30' : 'bg-red-100 border border-red-200'}`}>
            <Crosshair className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              BAS Engine
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Breach and Attack Simulation Platform
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchBASData}
            className={isDarkMode ? 'border-slate-700' : ''}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setShowAIGenerator(true)}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Campaign Generator
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Total Campaigns', value: campaigns.length || stats.totalCampaigns, icon: Target, color: 'blue' },
          { label: 'Active Simulations', value: simulations.filter(s => s.status === 'running').length, icon: Activity, color: 'green' },
          { label: 'Techniques Tested', value: '2,847', icon: Zap, color: 'orange' },
          { label: 'Detection Rate', value: '87%', icon: Shield, color: 'emerald' },
          { label: 'MITRE Coverage', value: '76%', icon: Layers, color: 'violet' },
        ].map((stat, index) => (
          <div 
            key={index}
            className={`rounded-xl p-4 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{stat.label}</p>
                <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
              </div>
              <div className={`p-2 rounded-lg bg-${stat.color}-500/10`}>
                <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className={`rounded-lg p-1 overflow-x-auto ${isDarkMode ? 'bg-slate-900' : 'bg-slate-100'}`}>
          <TabsList className="inline-flex w-max gap-1 bg-transparent">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <BarChart3 className="w-4 h-4 mr-1.5" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Target className="w-4 h-4 mr-1.5" />
              Campaigns
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Skull className="w-4 h-4 mr-1.5" />
              Scenarios
            </TabsTrigger>
            <TabsTrigger value="builder" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <GitBranch className="w-4 h-4 mr-1.5" />
              Builder
            </TabsTrigger>
            <TabsTrigger value="agentic" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Brain className="w-4 h-4 mr-1.5" />
              Agentic
            </TabsTrigger>
            <TabsTrigger value="simulations" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Activity className="w-4 h-4 mr-1.5" />
              Simulations
            </TabsTrigger>
            <TabsTrigger value="library" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Database className="w-4 h-4 mr-1.5" />
              Library
            </TabsTrigger>
            <TabsTrigger value="custom-techniques" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Bug className="w-4 h-4 mr-1.5" />
              Custom
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <TrendingUp className="w-4 h-4 mr-1.5" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="schedules" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Calendar className="w-4 h-4 mr-1.5" />
              Schedules
            </TabsTrigger>
            <TabsTrigger value="navigator" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Network className="w-4 h-4 mr-1.5" />
              Navigator
            </TabsTrigger>
            <TabsTrigger value="realtime" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <Activity className="w-4 h-4 mr-1.5" />
              Real-time
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="bas-reports-tab" className="data-[state=active]:bg-red-500 data-[state=active]:text-white whitespace-nowrap">
              <FileText className="w-4 h-4 mr-1.5" />
              Reports
            </TabsTrigger>
          </TabsList>
        </div>

        {/* BAS Dashboard Tab - Consolidated Results */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div 
              className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
              onClick={() => setActiveTab('simulations')}
              data-testid="dashboard-total-simulations"
            >
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-blue-500" />
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Simulations</span>
              </div>
              <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{simulations.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Click to view details</p>
            </div>
            <div 
              className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
              onClick={() => setActiveTab('campaigns')}
              data-testid="dashboard-active-campaigns"
            >
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-red-500" />
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Active Campaigns</span>
              </div>
              <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{campaigns.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Click to view details</p>
            </div>
            <div 
              className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${isDarkMode ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30' : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'}`}
              onClick={() => setActiveTab('analytics')}
              data-testid="dashboard-detection-rate"
            >
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-green-500" />
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Avg Detection Rate</span>
              </div>
              <p className="text-3xl font-bold text-green-500">
                {simulations.length > 0 
                  ? Math.round(simulations.reduce((acc, s) => acc + ((s.techniques_detected || 0) / Math.max(s.techniques_total || 1, 1) * 100), 0) / simulations.length)
                  : 0}%
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Across all simulations</p>
            </div>
            <div 
              className={`rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${isDarkMode ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30' : 'bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200'}`}
              onClick={() => setActiveTab('navigator')}
              data-testid="dashboard-techniques-tested"
            >
              <div className="flex items-center gap-2 mb-2">
                <Bug className="w-5 h-5 text-orange-500" />
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Techniques Tested</span>
              </div>
              <p className="text-3xl font-bold text-orange-500">
                {simulations.reduce((acc, s) => acc + (s.techniques_total || 0), 0)}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>MITRE ATT&CK coverage</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Detection Trend Chart */}
            <div 
              className={`rounded-xl p-5 cursor-pointer ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
              onClick={() => setActiveTab('analytics')}
            >
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Detection Rate Trend
              </h3>
              <div className="h-40 flex items-end gap-2">
                {simulations.slice(-7).map((sim, idx) => {
                  const rate = sim.techniques_total > 0 
                    ? Math.round((sim.techniques_detected || 0) / sim.techniques_total * 100) 
                    : 0;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div 
                        className={`w-full rounded-t transition-all ${rate >= 70 ? 'bg-green-500' : rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ height: `${Math.max(rate, 5)}%` }}
                        title={`${rate}% - ${sim.campaign_name || 'Simulation'}`}
                      />
                      <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{rate}%</span>
                    </div>
                  );
                })}
                {simulations.length === 0 && (
                  <div className={`flex-1 text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    No simulation data yet
                  </div>
                )}
              </div>
              <p className={`text-xs text-center mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Last {Math.min(simulations.length, 7)} simulations • Click for full analytics
              </p>
            </div>

            {/* Severity Distribution */}
            <div 
              className={`rounded-xl p-5 cursor-pointer ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
              onClick={() => setActiveTab('simulations')}
            >
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Simulation Results Breakdown
              </h3>
              <div className="space-y-3">
                {(() => {
                  const completed = simulations.filter(s => s.status === 'completed').length;
                  const running = simulations.filter(s => s.status === 'running').length;
                  const failed = simulations.filter(s => s.status === 'failed').length;
                  const total = simulations.length || 1;
                  return (
                    <>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Completed</span>
                          <span className="text-sm text-green-500">{completed}</span>
                        </div>
                        <div className={`h-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${(completed/total)*100}%` }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Running</span>
                          <span className="text-sm text-blue-500">{running}</span>
                        </div>
                        <div className={`h-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${(running/total)*100}%` }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Failed</span>
                          <span className="text-sm text-red-500">{failed}</span>
                        </div>
                        <div className={`h-2 rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                          <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${(failed/total)*100}%` }} />
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Recent Activity Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Simulations */}
            <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Recent Simulations
                </h3>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('simulations')} className={isDarkMode ? 'border-slate-700' : ''}>
                  View All
                </Button>
              </div>
              <div className="space-y-2">
                {simulations.slice(0, 5).map((sim, idx) => (
                  <div 
                    key={sim.id || idx}
                    className={`p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}
                    onClick={() => viewSimulationResults(sim)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        sim.status === 'completed' ? 'bg-green-500' :
                        sim.status === 'running' ? 'bg-blue-500 animate-pulse' :
                        'bg-red-500'
                      }`} />
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {sim.campaign_name || 'Simulation'}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {sim.techniques_total || 0} techniques
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${
                        (sim.techniques_detected || 0) / Math.max(sim.techniques_total || 1, 1) >= 0.7 ? 'text-green-500' : 'text-orange-500'
                      }`}>
                        {Math.round((sim.techniques_detected || 0) / Math.max(sim.techniques_total || 1, 1) * 100)}%
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>detected</p>
                    </div>
                  </div>
                ))}
                {simulations.length === 0 && (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No simulations yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Agentic Workflow Results */}
            <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Agentic Workflow Results
                </h3>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('agentic')} className={isDarkMode ? 'border-slate-700' : ''}>
                  View All
                </Button>
              </div>
              <div className="space-y-2">
                {agenticLogs.slice(0, 5).map((log, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[10px] ${
                        log.agent === 'validator' ? 'bg-blue-500/20 text-blue-400' :
                        log.agent === 'remediator' ? 'bg-green-500/20 text-green-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {log.agent}
                      </Badge>
                      <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'N/A'}
                      </span>
                    </div>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {log.message}
                    </p>
                  </div>
                ))}
                {agenticLogs.length === 0 && (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    <Brain className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No agentic workflow results yet</p>
                    <p className="text-xs mt-1">Run an agentic attack to see results</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Quick Actions
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setActiveTab('campaigns')} className="bg-red-600 hover:bg-red-700">
                <Target className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
              <Button onClick={() => setActiveTab('agentic')} variant="outline" className={isDarkMode ? 'border-slate-700' : ''}>
                <Brain className="w-4 h-4 mr-2" />
                Run Agentic Attack
              </Button>
              <Button onClick={() => setActiveTab('scenarios')} variant="outline" className={isDarkMode ? 'border-slate-700' : ''}>
                <Skull className="w-4 h-4 mr-2" />
                Prebuilt Scenarios
              </Button>
              <Button onClick={() => setActiveTab('navigator')} variant="outline" className={isDarkMode ? 'border-slate-700' : ''}>
                <Network className="w-4 h-4 mr-2" />
                MITRE Navigator
              </Button>
              <Button onClick={fetchMitreCoverage} variant="outline" className={isDarkMode ? 'border-slate-700' : ''}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Coverage
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Attack Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {campaigns.map((campaign) => (
              <div 
                key={campaign.id}
                className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Crosshair className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {campaign.name}
                    </h3>
                  </div>
                  {getStatusBadge(campaign.status)}
                </div>
                
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  {campaign.description}
                </p>
                
                {/* Tactics Tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {campaign.tactics?.slice(0, 4).map((tacticId) => {
                    const tactic = MITRE_TACTICS.find(t => t.id === tacticId);
                    return tactic ? (
                      <Badge key={tacticId} variant="outline" className="text-[10px]">
                        {tactic.name}
                      </Badge>
                    ) : null;
                  })}
                  {campaign.tactics?.length > 4 && (
                    <Badge variant="outline" className="text-[10px]">
                      +{campaign.tactics.length - 4} more
                    </Badge>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className={`text-center p-2 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {campaign.techniques_count}
                    </p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Techniques</p>
                  </div>
                  <div className={`text-center p-2 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className={`text-lg font-bold ${campaign.detection_rate >= 80 ? 'text-green-500' : campaign.detection_rate >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {campaign.detection_rate}%
                    </p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Detection</p>
                  </div>
                  <div className={`text-center p-2 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                      {campaign.risk_score}
                    </p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Risk Score</p>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={selectedForCompare.includes(campaign.id) ? "default" : "outline"}
                    className={selectedForCompare.includes(campaign.id) 
                      ? "bg-violet-600 hover:bg-violet-700" 
                      : isDarkMode ? 'border-slate-700' : ''}
                    onClick={() => toggleCompareSelection(campaign.id)}
                    data-testid={`compare-btn-${campaign.id}`}
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={() => startSimulation(campaign.id)}
                    disabled={runningSimulation === campaign.id}
                    data-testid={`run-sim-btn-${campaign.id}`}
                  >
                    {runningSimulation === campaign.id ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-1" />
                    )}
                    Run
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className={isDarkMode ? 'border-slate-700' : ''}
                    onClick={() => openCampaignConfig(campaign)}
                    data-testid={`config-btn-${campaign.id}`}
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {/* Add New Campaign Card */}
            <div 
              onClick={() => setShowAIGenerator(true)}
              className={`rounded-xl p-5 border-2 border-dashed cursor-pointer transition-all ${
                isDarkMode 
                  ? 'border-slate-700 hover:border-red-500/50 hover:bg-slate-900/30' 
                  : 'border-slate-300 hover:border-red-500/50 hover:bg-slate-50'
              }`}
            >
              <div className="flex flex-col items-center justify-center h-full min-h-[200px]">
                <div className={`p-3 rounded-full mb-3 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  <Plus className={`w-6 h-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                </div>
                <p className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Create New Campaign
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  Use AI or manual configuration
                </p>
              </div>
            </div>
          </div>
          
          {/* Compare Button - shows when campaigns selected */}
          {selectedForCompare.length >= 2 && (
            <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 ${isDarkMode ? 'bg-slate-800' : 'bg-white'} rounded-xl shadow-xl border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'} p-4 flex items-center gap-4`}>
              <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {selectedForCompare.length} campaigns selected
              </span>
              <Button 
                onClick={compareCampaigns}
                className="bg-gradient-to-r from-violet-600 to-purple-600"
                data-testid="compare-campaigns-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Compare Campaigns
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedForCompare([])}
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
          )}
        </TabsContent>

        {/* NEW: Prebuilt Scenarios Tab */}
        <TabsContent value="scenarios" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Prebuilt Attack Scenarios
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Ready-to-deploy attack simulations based on real-world threat actors
                </p>
              </div>
              <Select value={scenarioFilter} onValueChange={setScenarioFilter}>
                <SelectTrigger className={`w-48 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="nation_state">Nation State</SelectItem>
                  <SelectItem value="cybercrime">Cybercrime</SelectItem>
                  <SelectItem value="social_engineering">Social Engineering</SelectItem>
                  <SelectItem value="insider_threat">Insider Threat</SelectItem>
                  <SelectItem value="cloud_attack">Cloud Attack</SelectItem>
                  <SelectItem value="supply_chain">Supply Chain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {scenarios
                .filter(s => scenarioFilter === 'all' || s.category === scenarioFilter)
                .map((scenario) => (
                <div 
                  key={scenario.id}
                  className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}
                  data-testid={`scenario-card-${scenario.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Skull className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                      <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {scenario.name}
                      </h4>
                    </div>
                    <Badge className={`text-[10px] border ${getDifficultyColor(scenario.difficulty)}`}>
                      {scenario.difficulty}
                    </Badge>
                  </div>
                  
                  <p className={`text-xs mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {scenario.description}
                  </p>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="text-[10px]">
                      {getCategoryLabel(scenario.category)}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {scenario.techniques?.length || 0} techniques
                    </Badge>
                  </div>
                  
                  <div className={`text-xs mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Threat Actor: <span className="font-medium">{scenario.threat_actor}</span>
                  </div>
                  
                  <Button 
                    size="sm" 
                    className="w-full bg-red-600 hover:bg-red-700"
                    onClick={() => deployScenario(scenario.id)}
                    disabled={deployingScenario === scenario.id}
                    data-testid={`deploy-scenario-${scenario.id}`}
                  >
                    {deployingScenario === scenario.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Deploy Scenario
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Active Simulations Tab - Enhanced with Results & Recommendations */}
        <TabsContent value="simulations" className="space-y-4">
          {simulations.length === 0 ? (
            <div className={`rounded-xl p-12 text-center ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200'}`}>
              <Activity className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} />
              <p className={`text-lg font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                No Simulations Yet
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                Start a campaign to begin attack simulation
              </p>
            </div>
          ) : (
            simulations.map((sim) => (
              <div 
                key={sim.id}
                className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}
                data-testid={`simulation-${sim.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${sim.status === 'running' ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                      {sim.status === 'running' ? (
                        <Activity className="w-5 h-5 text-green-500 animate-pulse" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                      )}
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {sim.campaign_name}
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {sim.status === 'running' ? `Current: ${sim.current_technique || 'Initializing...'}` : `Completed ${sim.completed_at ? new Date(sim.completed_at).toLocaleString() : ''}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(sim.status)}
                    {sim.status === 'completed' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className={isDarkMode ? 'border-slate-700' : ''}
                        onClick={() => viewSimulationResults(sim)}
                        data-testid={`view-results-${sim.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Results
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Progress</span>
                    <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{sim.progress || Math.round((sim.techniques_executed || 0) / Math.max(sim.techniques_total, 1) * 100)}%</span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${sim.status === 'running' ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${sim.progress || Math.round((sim.techniques_executed || 0) / Math.max(sim.techniques_total, 1) * 100)}%` }}
                    />
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-4">
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {sim.techniques_executed || sim.techniques_completed || 0}/{sim.techniques_total || 10}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Techniques Run</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className="text-2xl font-bold text-green-500">{sim.techniques_detected || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Detected</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className="text-2xl font-bold text-blue-500">{sim.techniques_blocked || 0}</p>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Blocked</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className="text-2xl font-bold text-red-500">
                      {(sim.techniques_total || 10) - (sim.techniques_detected || 0)}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Evaded</p>
                  </div>
                </div>
                
                {/* Quick Recommendations Preview (for completed simulations) */}
                {sim.status === 'completed' && (
                  <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className={`w-4 h-4 ${(sim.techniques_detected || 0) / Math.max(sim.techniques_total, 1) < 0.7 ? 'text-red-500' : 'text-yellow-500'}`} />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Quick Assessment
                      </span>
                    </div>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Detection Rate: <span className={`font-medium ${(sim.techniques_detected || 0) / Math.max(sim.techniques_total, 1) >= 0.8 ? 'text-green-500' : (sim.techniques_detected || 0) / Math.max(sim.techniques_total, 1) >= 0.5 ? 'text-yellow-500' : 'text-red-500'}`}>
                        {((sim.techniques_detected || 0) / Math.max(sim.techniques_total, 1) * 100).toFixed(0)}%
                      </span>
                      {' • '}
                      Block Rate: <span className="font-medium text-blue-500">
                        {((sim.techniques_blocked || 0) / Math.max(sim.techniques_total, 1) * 100).toFixed(0)}%
                      </span>
                    </p>
                  </div>
                )}
                
                {sim.status === 'running' && (
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" className={isDarkMode ? 'border-slate-700' : ''}>
                      <Pause className="w-4 h-4 mr-1" />
                      Pause
                    </Button>
                    <Button size="sm" variant="destructive">
                      <Square className="w-4 h-4 mr-1" />
                      Stop
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </TabsContent>

        {/* MITRE ATT&CK Heatmap Tab */}
        <TabsContent value="mitre" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              MITRE ATT&CK Coverage Heatmap
            </h3>
            
            <div className="space-y-2">
              {MITRE_TACTICS.map((tactic) => {
                const techniques = ATTACK_TECHNIQUES[tactic.id] || [];
                // Get real coverage from mitreCoverage state, default to 0 if no data
                const tacticData = mitreCoverage?.tactic_coverage?.[tactic.id] || {};
                const coverage = tacticData.detection_rate || 0;
                
                return (
                  <div 
                    key={tactic.id}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedTactic === tactic.id 
                        ? isDarkMode ? 'bg-slate-800 border border-red-500/50' : 'bg-slate-100 border border-red-500/50'
                        : isDarkMode ? 'bg-slate-800/50 hover:bg-slate-800' : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => setSelectedTactic(selectedTactic === tactic.id ? null : tactic.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full bg-${tactic.color}-500`} />
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {tactic.name}
                        </span>
                        <Badge variant="outline" className="text-[10px]">{tactic.id}</Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {techniques.length} techniques
                        </span>
                        <div className="w-24 h-2 rounded-full overflow-hidden bg-slate-700">
                          <div 
                            className={`h-full bg-${tactic.color}-500`}
                            style={{ width: `${coverage}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {coverage}%
                        </span>
                        {selectedTactic === tactic.id ? (
                          <ChevronUp className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                        ) : (
                          <ChevronDown className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                        )}
                      </div>
                    </div>
                    
                    {selectedTactic === tactic.id && techniques.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <div className="grid grid-cols-2 gap-2">
                          {techniques.map((tech) => (
                            <div 
                              key={tech.id}
                              className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${getSeverityColor(tech.severity)}`} />
                                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                  {tech.id}
                                </span>
                              </div>
                              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {tech.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Attack Library Tab - SELECTABLE */}
        <TabsContent value="library" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Attack Technique Library
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Click techniques to select them for custom campaigns
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input 
                  placeholder="Search techniques..." 
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  className={`w-64 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                  data-testid="library-search-input"
                />
                <Select value={libraryTacticFilter} onValueChange={setLibraryTacticFilter}>
                  <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
                    <SelectValue placeholder="All Tactics" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tactics</SelectItem>
                    {MITRE_TACTICS.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Selected Techniques Summary */}
            {selectedTechniques.length > 0 && (
              <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-violet-500/10 border border-violet-500/30' : 'bg-violet-50 border border-violet-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-violet-500" />
                    <span className={`font-medium ${isDarkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                      {selectedTechniques.length} technique{selectedTechniques.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setSelectedTechniques([])}
                      className={isDarkMode ? 'text-slate-400 hover:text-white' : ''}
                    >
                      Clear All
                    </Button>
                    <Button 
                      size="sm"
                      className="bg-gradient-to-r from-violet-600 to-purple-600"
                      onClick={() => setShowCreateCustom(true)}
                      data-testid="create-custom-campaign-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Create Campaign
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTechniques.slice(0, 10).map(techId => (
                    <Badge key={techId} variant="outline" className="text-[10px] bg-violet-500/20">
                      {techId}
                    </Badge>
                  ))}
                  {selectedTechniques.length > 10 && (
                    <Badge variant="outline" className="text-[10px]">
                      +{selectedTechniques.length - 10} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {getFilteredTechniques().map((tech) => {
                const isSelected = selectedTechniques.includes(tech.id);
                return (
                  <div 
                    key={tech.id}
                    onClick={() => toggleTechniqueSelection(tech.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-all border-2 ${
                      isSelected 
                        ? 'border-violet-500 bg-violet-500/10' 
                        : isDarkMode 
                          ? 'border-transparent bg-slate-800 hover:bg-slate-700 hover:border-slate-600' 
                          : 'border-transparent bg-slate-50 hover:bg-slate-100 hover:border-slate-300'
                    }`}
                    data-testid={`technique-card-${tech.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <Badge className={getSeverityColor(tech.severity) + ' text-white text-[10px]'}>
                        {tech.severity}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {tech.id}
                        </span>
                        {isSelected && <CheckCircle className="w-4 h-4 text-violet-500" />}
                      </div>
                    </div>
                    <p className={`font-medium text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {tech.name}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {tech.tacticName}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Analytics & Trends Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Campaign Comparison */}
            {comparisonData && (
              <div className={`lg:col-span-2 rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Campaign Comparison
                  </h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setComparisonData(null)}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                  {comparisonData.comparison?.map((camp) => (
                    <div 
                      key={camp.campaign_id}
                      className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}
                    >
                      <h4 className={`font-medium text-sm mb-2 truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {camp.campaign_name}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Detection</span>
                          <span className={`font-bold ${camp.metrics.detection_rate >= 80 ? 'text-green-500' : camp.metrics.detection_rate >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                            {camp.metrics.detection_rate}%
                          </span>
                        </div>
                        <div className={`h-2 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                          <div 
                            className={`h-full rounded-full ${camp.metrics.detection_rate >= 80 ? 'bg-green-500' : camp.metrics.detection_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${camp.metrics.detection_rate}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Block Rate</span>
                          <span className="font-bold text-blue-500">{camp.metrics.block_rate}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {comparisonData.summary && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Best Detection</p>
                        <p className={`font-semibold text-green-500`}>{comparisonData.summary.best_detection}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Worst Detection</p>
                        <p className={`font-semibold text-red-500`}>{comparisonData.summary.worst_detection}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Avg Detection</p>
                        <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{comparisonData.summary.avg_detection_rate}%</p>
                      </div>
                      <div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Avg Block Rate</p>
                        <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{comparisonData.summary.avg_block_rate}%</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Detection Trends Chart */}
            <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Detection Rate Trend
                </h3>
                {trendsData?.trend && (
                  <Badge className={`${trendsData.trend.direction === 'improving' ? 'bg-green-500/20 text-green-500' : trendsData.trend.direction === 'declining' ? 'bg-red-500/20 text-red-500' : 'bg-gray-500/20 text-gray-500'}`}>
                    {trendsData.trend.direction === 'improving' ? '↑' : trendsData.trend.direction === 'declining' ? '↓' : '→'} {Math.abs(trendsData.trend.change_percentage)}%
                  </Badge>
                )}
              </div>
              
              {loadingTrends ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
                </div>
              ) : trendsData?.timeline ? (
                <div className="h-48">
                  <div className="flex items-end justify-between h-40 gap-1 px-2">
                    {trendsData.timeline.slice(-14).map((day, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center min-w-[12px]">
                        <div 
                          className="w-full min-w-[8px] bg-gradient-to-t from-violet-600 to-violet-400 rounded-t transition-all hover:from-violet-500 hover:to-violet-300"
                          style={{ height: `${Math.max(day.detection_rate * 0.95, 5)}%` }}
                          title={`${day.date}: ${Math.round(day.detection_rate)}%`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 px-2">
                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      {trendsData.timeline[Math.max(0, trendsData.timeline.length - 14)]?.date}
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      {trendsData.timeline[trendsData.timeline.length - 1]?.date}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-slate-500">
                  No trend data available
                </div>
              )}
            </div>

            {/* Trend Summary Stats */}
            <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                30-Day Summary
              </h3>
              
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Current Avg Detection</span>
                    <span className={`text-2xl font-bold ${trendsData?.trend?.current_avg_detection >= 80 ? 'text-green-500' : 'text-yellow-500'}`}>
                      {trendsData?.trend?.current_avg_detection || 0}%
                    </span>
                  </div>
                </div>
                
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Simulations</span>
                    <span className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {trendsData?.total_simulations || 0}
                    </span>
                  </div>
                </div>
                
                {trendsData?.summary?.highest_detection && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Best Day</p>
                    <p className={`font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                      {trendsData.summary.highest_detection.date}: {trendsData.summary.highest_detection.detection_rate}%
                    </p>
                  </div>
                )}
                
                {trendsData?.summary?.lowest_detection && (
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Worst Day</p>
                    <p className={`font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                      {trendsData.summary.lowest_detection.date}: {trendsData.summary.lowest_detection.detection_rate}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Results & Analytics Tab - Keep for backwards compatibility */}
        <TabsContent value="results" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Detection Summary */}
            <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Detection Summary
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'EDR Detection', value: 89, color: 'emerald' },
                  { label: 'SIEM Alerts', value: 76, color: 'blue' },
                  { label: 'Network IDS', value: 82, color: 'violet' },
                  { label: 'Endpoint AV', value: 94, color: 'green' },
                ].map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{item.label}</span>
                      <span className={`font-medium text-${item.color}-500`}>{item.value}%</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                      <div className={`h-full bg-${item.color}-500`} style={{ width: `${item.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Score Breakdown */}
            <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Risk Score Analysis
              </h3>
              <div className="flex items-center justify-center h-40">
                <div className="text-center">
                  <div className="text-5xl font-bold text-orange-500 mb-2">7.8</div>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Overall Risk Score
                  </p>
                  <Badge className="mt-2 bg-orange-500/20 text-orange-500 border border-orange-500/30">
                    High Risk
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Agentic Workflow Tab - REAL-TIME WEBSOCKET */}
        <TabsContent value="agentic" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Agentic Attack Workflow
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  AI-powered 4-stage attack simulation with real-time updates
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Select 
                  value={selectedCampaignForAgentic || ''} 
                  onValueChange={setSelectedCampaignForAgentic}
                >
                  <SelectTrigger className={`w-64 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
                    <SelectValue placeholder="Select campaign..." />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  className="bg-gradient-to-r from-violet-600 to-purple-600"
                  onClick={() => startAgenticWorkflow()}
                  disabled={runningWorkflow || campaigns.length === 0}
                  data-testid="start-agentic-workflow-btn"
                >
                  {runningWorkflow ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Start Agentic Run
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Workflow Stages with Real-time Status */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { stage: 'planner', label: 'Planner', icon: Brain, desc: 'Analyzes target and creates attack plan', color: 'violet' },
                { stage: 'executor', label: 'Executor', icon: Zap, desc: 'Runs attack techniques sequentially', color: 'orange' },
                { stage: 'validator', label: 'Validator', icon: Shield, desc: 'Validates detection and coverage', color: 'emerald' },
                { stage: 'remediator', label: 'Remediator', icon: CheckCircle, desc: 'Generates remediation recommendations', color: 'blue' },
              ].map((item, index) => {
                const isActive = agenticWorkflow?.currentStage === item.stage;
                const isCompleted = workflowUpdates.some(u => u.stage === item.stage && u.status === 'completed');
                const isRunning = workflowUpdates.some(u => u.stage === item.stage && u.status === 'running');
                
                return (
                  <div 
                    key={item.stage}
                    className={`relative rounded-xl p-4 transition-all ${
                      isActive 
                        ? `border-2 border-${item.color}-500 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}` 
                        : isCompleted
                          ? `border-2 border-green-500/50 ${isDarkMode ? 'bg-green-500/5' : 'bg-green-50'}`
                          : isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
                    }`}
                    data-testid={`workflow-stage-${item.stage}`}
                  >
                    {index < 3 && (
                      <div className="absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                        <ArrowRight className={`w-6 h-6 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                      isCompleted ? 'bg-green-500/20' : `bg-${item.color}-500/20`
                    }`}>
                      {isRunning ? (
                        <Loader2 className={`w-5 h-5 text-${item.color}-500 animate-spin`} />
                      ) : isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <item.icon className={`w-5 h-5 text-${item.color}-500`} />
                      )}
                    </div>
                    <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {item.label}
                    </h4>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {item.desc}
                    </p>
                    {isRunning && (
                      <Badge className="mt-2 bg-violet-500/20 text-violet-500 border border-violet-500/30 animate-pulse">
                        In Progress
                      </Badge>
                    )}
                    {isCompleted && (
                      <Badge className="mt-2 bg-green-500/20 text-green-500 border border-green-500/30">
                        Completed
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Real-time Updates Feed */}
            {workflowUpdates.length > 0 && (
              <div className={`rounded-lg p-4 mb-4 ${isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Live Updates
                  </h4>
                  <Badge className="bg-green-500/20 text-green-500 border border-green-500/30 animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2" />
                    Live
                  </Badge>
                </div>
                <ScrollArea className="h-48">
                  <div className="space-y-2">
                    {[...workflowUpdates].reverse().map((update, i) => (
                      <div 
                        key={i}
                        className={`flex items-start gap-3 p-2 rounded ${isDarkMode ? 'bg-slate-900' : 'bg-white'} animate-fadeIn`}
                      >
                        {update.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                        ) : update.status === 'running' ? (
                          <Loader2 className="w-4 h-4 text-violet-500 mt-0.5 animate-spin" />
                        ) : (
                          <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm capitalize ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {update.stage}
                            </span>
                            <Badge variant="outline" className="text-[10px]">
                              {update.status}
                            </Badge>
                          </div>
                          {update.data?.message && (
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {update.data.message}
                            </p>
                          )}
                          {update.data?.detection_rate !== undefined && (
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Detection Rate: <span className="font-medium text-green-500">{update.data.detection_rate}%</span>
                            </p>
                          )}
                          {update.data?.techniques_planned && (
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Techniques planned: {update.data.techniques_planned}
                            </p>
                          )}
                          {update.data?.progress !== undefined && (
                            <div className="mt-1">
                              <div className={`h-1.5 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                <div 
                                  className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all"
                                  style={{ width: `${update.data.progress}%` }}
                                />
                              </div>
                              <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {update.data.progress}% complete
                              </span>
                            </div>
                          )}
                        </div>
                        <span className={`text-[10px] ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                          {new Date(update.receivedAt).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Sample Workflow Output (when no live updates) */}
            {workflowUpdates.length === 0 && (
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Recent Workflow Execution
                </h4>
                <div className="space-y-2">
                  {[
                    { stage: 'Planner', status: 'completed', output: 'Generated 5-phase attack plan targeting web application' },
                    { stage: 'Executor', status: 'completed', output: 'Executed 12 techniques, 8 successful, 4 blocked' },
                    { stage: 'Validator', status: 'completed', output: 'Detection rate: 78%, 3 gaps identified' },
                    { stage: 'Remediator', status: 'completed', output: '4 critical recommendations, 2 SIEM rules proposed' },
                  ].map((item) => (
                    <div 
                      key={item.stage}
                      className={`flex items-center gap-3 p-2 rounded ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
                    >
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className={`font-medium text-sm w-24 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {item.stage}
                      </span>
                      <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {item.output}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Defense Validation Tab */}
        {/* Schedules Tab - Enhanced with Auto-Compare & Alerts */}
        <TabsContent value="schedules" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Scheduled Campaign Automation
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Automated simulations with trend comparison and detection alerts
                </p>
              </div>
              <Button className="bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                New Schedule
              </Button>
            </div>

            {/* Schedule List with Auto-Compare Stats */}
            <div className="space-y-3">
              {schedules.map((schedule) => (
                <div 
                  key={schedule.id}
                  className={`p-4 rounded-lg ${schedule.alert ? (isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200') : (isDarkMode ? 'bg-slate-800' : 'bg-slate-50')}`}
                  data-testid={`schedule-${schedule.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${schedule.alert ? 'bg-red-500/20' : (isDarkMode ? 'bg-slate-700' : 'bg-white')}`}>
                        <Calendar className={`w-5 h-5 ${schedule.alert ? 'text-red-500' : (isDarkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {schedule.campaign_name}
                          </h4>
                          {schedule.alert && (
                            <Badge className="bg-red-500 text-white text-[10px] animate-pulse">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Detection Dropped
                            </Badge>
                          )}
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {schedule.schedule_type} • Next: {schedule.next_run}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {/* Auto-Compare Stats */}
                      {schedule.last_detection_rate !== undefined && (
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Detection</p>
                            <p className={`text-lg font-bold ${schedule.last_detection_rate >= 80 ? 'text-green-500' : schedule.last_detection_rate >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                              {schedule.last_detection_rate}%
                            </p>
                          </div>
                          {schedule.previous_detection_rate !== undefined && (
                            <div className="flex items-center gap-1">
                              {schedule.last_detection_rate > schedule.previous_detection_rate ? (
                                <TrendingUp className="w-4 h-4 text-green-500" />
                              ) : schedule.last_detection_rate < schedule.previous_detection_rate ? (
                                <TrendingDown className="w-4 h-4 text-red-500" />
                              ) : (
                                <ArrowRight className="w-4 h-4 text-slate-400" />
                              )}
                              <span className={`text-sm font-medium ${
                                schedule.last_detection_rate > schedule.previous_detection_rate 
                                  ? 'text-green-500' 
                                  : schedule.last_detection_rate < schedule.previous_detection_rate 
                                    ? 'text-red-500' 
                                    : 'text-slate-400'
                              }`}>
                                {schedule.last_detection_rate > schedule.previous_detection_rate ? '+' : ''}
                                {schedule.last_detection_rate - schedule.previous_detection_rate}%
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {schedule.runs} runs
                        </Badge>
                        <Badge className={schedule.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}>
                          {schedule.status}
                        </Badge>
                        
                        {/* Resume/Pause Button */}
                        {schedule.status === 'paused' ? (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => resumeSchedule(schedule.id)}
                            className="text-green-500 hover:text-green-600 hover:bg-green-500/10"
                            data-testid={`resume-schedule-${schedule.id}`}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => pauseSchedule(schedule.id)}
                            className="text-yellow-500 hover:text-yellow-600 hover:bg-yellow-500/10"
                            data-testid={`pause-schedule-${schedule.id}`}
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        )}
                        
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openScheduleConfig(schedule)}
                          data-testid={`config-schedule-${schedule.id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Alert Threshold Info */}
            <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Bell className={`w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Auto-Compare & Alerts
                </h4>
              </div>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Schedules automatically compare detection rates with previous runs and alert when they drop below the threshold.
              </p>
              <div className="flex gap-2 flex-wrap">
                {['Daily at 2 AM', 'Weekly on Sunday', 'Monthly on 1st', 'Custom Cron'].map((option) => (
                  <Button key={option} variant="outline" size="sm" className={isDarkMode ? 'border-slate-700' : ''}>
                    {option}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Custom Techniques Tab */}
        <TabsContent value="custom-techniques" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Custom MITRE Techniques
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Define custom attack techniques for your organization's specific threats
                </p>
              </div>
              <Button 
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => setShowCustomTechnique(true)}
                data-testid="add-custom-technique-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Custom Technique
              </Button>
            </div>

            {/* Custom Techniques List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {customTechniques.length === 0 ? (
                <div className={`col-span-full text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <Bug className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No custom techniques defined yet</p>
                  <p className="text-sm">Create techniques specific to your threat landscape</p>
                </div>
              ) : (
                customTechniques.map((tech) => (
                  <div 
                    key={tech.id}
                    className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}
                    data-testid={`custom-technique-${tech.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <Badge className={getSeverityColor(tech.severity) + ' text-white text-[10px]'}>
                        {tech.severity}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <span className={`text-xs font-mono ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                          {tech.id}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => deleteCustomTechnique(tech.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <h4 className={`font-medium text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {tech.name}
                    </h4>
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {tech.description}
                    </p>
                    <Badge variant="outline" className="text-[10px]">
                      {MITRE_TACTICS.find(t => t.id === tech.tactic)?.name || tech.tactic}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Scenario Builder Tab */}
        <TabsContent value="builder" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Attack Scenario Builder
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Drag and drop techniques to create custom multi-phase attack scenarios
                </p>
              </div>
              <Button 
                className="bg-gradient-to-r from-violet-600 to-purple-600"
                onClick={saveBuilderScenario}
                disabled={!builderScenario.name || builderScenario.phases.every(p => p.techniques.length === 0)}
                data-testid="save-builder-scenario"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Scenario
              </Button>
            </div>

            {/* Scenario Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Scenario Name *
                </label>
                <Input 
                  placeholder="e.g., Multi-Stage APT Attack"
                  value={builderScenario.name}
                  onChange={(e) => setBuilderScenario(prev => ({ ...prev, name: e.target.value }))}
                  className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                  data-testid="builder-scenario-name"
                />
              </div>
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description
                </label>
                <Input 
                  placeholder="Describe the attack scenario..."
                  value={builderScenario.description}
                  onChange={(e) => setBuilderScenario(prev => ({ ...prev, description: e.target.value }))}
                  className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Technique Palette */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Technique Palette
                </h4>
                <Input 
                  placeholder="Search techniques..." 
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  className={`mb-3 ${isDarkMode ? 'bg-slate-700 border-slate-600' : ''}`}
                />
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {getFilteredTechniques().slice(0, 20).map((tech) => (
                      <div 
                        key={tech.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, tech)}
                        className={`p-2 rounded cursor-grab active:cursor-grabbing transition-all ${
                          isDarkMode 
                            ? 'bg-slate-700 hover:bg-slate-600 border border-slate-600' 
                            : 'bg-white hover:bg-slate-50 border border-slate-200'
                        }`}
                        data-testid={`palette-technique-${tech.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {tech.name}
                          </span>
                          <GripVertical className="w-3 h-3 text-slate-400" />
                        </div>
                        <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {tech.id}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Phase Builder */}
              <div className="col-span-2 space-y-4">
                {builderScenario.phases.map((phase, phaseIndex) => (
                  <div 
                    key={phaseIndex}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, phaseIndex)}
                    className={`rounded-lg p-4 min-h-[120px] transition-all border-2 border-dashed ${
                      isDarkMode 
                        ? 'bg-slate-800/50 border-slate-700 hover:border-violet-500/50' 
                        : 'bg-slate-50 border-slate-300 hover:border-violet-500/50'
                    }`}
                    data-testid={`phase-dropzone-${phaseIndex}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Input 
                        value={phase.name}
                        onChange={(e) => {
                          const newPhases = [...builderScenario.phases];
                          newPhases[phaseIndex].name = e.target.value;
                          setBuilderScenario(prev => ({ ...prev, phases: newPhases }));
                        }}
                        className={`w-48 h-8 text-sm font-medium ${isDarkMode ? 'bg-transparent border-slate-700' : 'bg-transparent'}`}
                      />
                      {builderScenario.phases.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 h-8"
                          onClick={() => removeBuilderPhase(phaseIndex)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    {phase.techniques.length === 0 ? (
                      <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Drag techniques here
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {phase.techniques.map((tech) => (
                          <Badge 
                            key={tech.id}
                            className="bg-violet-500/20 text-violet-400 border border-violet-500/30 pr-1"
                          >
                            {tech.name}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                              onClick={() => removeTechniqueFromPhase(phaseIndex, tech.id)}
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                <Button 
                  variant="outline" 
                  className={`w-full ${isDarkMode ? 'border-slate-700' : ''}`}
                  onClick={addBuilderPhase}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Phase
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* MITRE ATT&CK Navigator Tab */}
        <TabsContent value="navigator" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  MITRE ATT&CK Navigator
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Interactive coverage heatmap based on simulation results
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchMitreCoverage}
                disabled={loadingMitreCoverage}
                className={isDarkMode ? 'border-slate-700' : ''}
              >
                {loadingMitreCoverage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh Coverage
              </Button>
            </div>

            {/* Coverage Summary */}
            {mitreCoverage && (
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <p className={`text-3xl font-bold text-blue-500`}>
                    {mitreCoverage.summary?.total_techniques_tested || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Techniques Tested</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <p className={`text-3xl font-bold text-green-500`}>
                    {mitreCoverage.summary?.avg_detection_rate || 0}%
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Avg Detection Rate</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <p className={`text-3xl font-bold text-violet-500`}>
                    {mitreCoverage.summary?.total_simulations || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Simulations</p>
                </div>
              </div>
            )}
            
            {/* Interactive Heatmap Grid */}
            <div className="space-y-3">
              {MITRE_TACTICS.map((tactic) => {
                const tacticData = mitreCoverage?.tactic_coverage?.[tactic.id] || { 
                  detection_rate: 0, 
                  techniques_tested: 0,
                  coverage_level: 'none'
                };
                const isGap = mitreCoverage?.coverage_gaps?.includes(tactic.id);
                const detectionRate = tacticData.detection_rate || 0;
                
                return (
                  <div 
                    key={tactic.id}
                    data-testid={`navigator-tactic-${tactic.id}`}
                    className={`p-4 rounded-lg cursor-pointer transition-all ${
                      selectedTactic === tactic.id 
                        ? isDarkMode ? 'bg-slate-800 border-2 border-red-500/70 shadow-lg' : 'bg-slate-100 border-2 border-red-500/70 shadow-lg'
                        : isDarkMode ? 'bg-slate-800/50 hover:bg-slate-800 border border-slate-700' : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
                    } ${isGap ? 'ring-2 ring-red-500/30' : ''}`}
                    onClick={() => setSelectedTactic(selectedTactic === tactic.id ? null : tactic.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded ${getCoverageColor(tacticData.coverage_level, detectionRate)}`} />
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {tactic.name}
                        </span>
                        <Badge variant="outline" className="text-[10px]">{tactic.id}</Badge>
                        {isGap && (
                          <Badge className="bg-red-500/20 text-red-500 text-[10px]">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Gap
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {tacticData.techniques_tested} techniques
                        </span>
                        <div className="w-32 h-3 rounded-full overflow-hidden bg-slate-700">
                          <div 
                            className={`h-full transition-all duration-500 ${getCoverageColor(tacticData.coverage_level, detectionRate)}`}
                            style={{ width: `${detectionRate}%` }}
                          />
                        </div>
                        <span className={`text-sm font-semibold min-w-[45px] text-right ${
                          detectionRate >= 80 ? 'text-green-500' : 
                          detectionRate >= 60 ? 'text-yellow-500' : 
                          detectionRate >= 40 ? 'text-orange-500' : 'text-red-500'
                        }`}>
                          {detectionRate}%
                        </span>
                        {selectedTactic === tactic.id ? (
                          <ChevronUp className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                        ) : (
                          <ChevronDown className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                        )}
                      </div>
                    </div>
                    
                    {selectedTactic === tactic.id && (
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                          {(ATTACK_TECHNIQUES[tactic.id] || []).map((tech) => (
                            <div 
                              key={tech.id}
                              className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-slate-900' : 'bg-white'} border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${getSeverityColor(tech.severity)}`} />
                                <span className={`text-sm font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                  {tech.id}
                                </span>
                              </div>
                              <span className={`text-xs truncate max-w-[120px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {tech.name}
                              </span>
                            </div>
                          ))}
                        </div>
                        {tacticData.name && (
                          <p className={`mt-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Tactic: {tacticData.name} • Coverage Level: <span className="font-medium capitalize">{tacticData.coverage_level}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Coverage Legend</h4>
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>High (80%+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-yellow-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Medium (60-79%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-orange-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Low (40-59%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Critical (&lt;40%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`} />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Not Tested</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Real-time Metrics Tab */}
        <TabsContent value="realtime" className="space-y-4">
          <div className={`rounded-xl p-5 ${isDarkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Real-time Streaming Metrics
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Live monitoring of BAS activities and security events
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs text-green-500 font-medium">Live</span>
                </div>
              </div>
            </div>

            {/* Real-time Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-blue-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Active Simulations</span>
                </div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {simulations.filter(s => s.status === 'running').length}
                </p>
              </div>
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Detections Today</span>
                </div>
                <p className={`text-2xl font-bold text-green-500`}>
                  {stats?.detections_today || 0}
                </p>
              </div>
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Evasions Today</span>
                </div>
                <p className={`text-2xl font-bold text-red-500`}>
                  {stats?.evasions_today || 0}
                </p>
              </div>
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-violet-500" />
                  <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Avg Detection Rate</span>
                </div>
                <p className={`text-2xl font-bold text-violet-500`}>
                  {stats?.avg_detection_rate || 0}%
                </p>
              </div>
            </div>

            {/* Live Activity Feed */}
            <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <h4 className={`text-sm font-medium mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <Activity className="w-4 h-4" />
                Live Activity Feed
              </h4>
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {simulations.slice(0, 10).map((sim, idx) => (
                    <div 
                      key={sim.id || idx}
                      className={`p-3 rounded-lg flex items-center justify-between ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          sim.status === 'running' ? 'bg-blue-500 animate-pulse' :
                          sim.status === 'completed' ? 'bg-green-500' :
                          sim.status === 'failed' ? 'bg-red-500' : 'bg-slate-500'
                        }`} />
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {sim.campaign_name || 'Simulation'}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {sim.status === 'running' ? 'In Progress' : sim.status} • {sim.techniques_total || 0} techniques
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-semibold ${
                          (sim.techniques_detected || 0) / Math.max(sim.techniques_total || 1, 1) >= 0.7 ? 'text-green-500' : 'text-orange-500'
                        }`}>
                          {((sim.techniques_detected || 0) / Math.max(sim.techniques_total || 1, 1) * 100).toFixed(0)}% detected
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {sim.started_at ? new Date(sim.started_at).toLocaleTimeString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                  {simulations.length === 0 && (
                    <div className={`text-center py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No recent activity</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Performance Trends Mini Chart */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Detection Trend (7 Days)
                </h4>
                <div className="flex items-end gap-1 h-16">
                  {[65, 72, 68, 75, 80, 78, 82].map((val, idx) => (
                    <div 
                      key={idx}
                      className="flex-1 bg-green-500 rounded-t transition-all hover:bg-green-400"
                      style={{ height: `${val}%` }}
                      title={`${val}%`}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-2">
                  <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Mon</span>
                  <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sun</span>
                </div>
              </div>
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Simulations Run (7 Days)
                </h4>
                <div className="flex items-end gap-1 h-16">
                  {[3, 5, 2, 7, 4, 6, 8].map((val, idx) => (
                    <div 
                      key={idx}
                      className="flex-1 bg-blue-500 rounded-t transition-all hover:bg-blue-400"
                      style={{ height: `${val * 12}%` }}
                      title={`${val} simulations`}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-2">
                  <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Mon</span>
                  <span className={`text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sun</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* BAS Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className={`rounded-xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 bg-gradient-to-br from-red-600/20 to-orange-600/20 border rounded-lg flex items-center justify-center ${isDarkMode ? 'border-red-500/30' : 'border-red-300'}`}>
                <FileText className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className={`font-semibold text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>BAS Engine Reports</h3>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Generate comprehensive attack simulation reports</p>
              </div>
            </div>
            
            {/* Report Types */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Executive Summary Report */}
              <div 
                className={`rounded-xl p-5 border cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-slate-900/50 border-slate-800 hover:border-red-500/50' : 'bg-slate-50 border-slate-200 hover:border-red-500'}`}
                onClick={() => generateComprehensiveReport('executive')}
                data-testid="bas-executive-report-btn"
              >
                <div className="flex items-center gap-3 mb-3">
                  <BarChart3 className="w-8 h-8 text-red-500" />
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Executive Summary</h4>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>High-level overview for stakeholders</p>
                  </div>
                </div>
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <li>• Overall security posture score</li>
                  <li>• Detection rate trends</li>
                  <li>• Critical gaps identified</li>
                  <li>• Strategic recommendations</li>
                </ul>
              </div>
              
              {/* Technical Detail Report */}
              <div 
                className={`rounded-xl p-5 border cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-slate-900/50 border-slate-800 hover:border-orange-500/50' : 'bg-slate-50 border-slate-200 hover:border-orange-500'}`}
                onClick={() => generateComprehensiveReport('technical')}
                data-testid="bas-technical-report-btn"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Bug className="w-8 h-8 text-orange-500" />
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Technical Detail</h4>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>In-depth technical analysis</p>
                  </div>
                </div>
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <li>• MITRE ATT&CK mapping</li>
                  <li>• Technique-by-technique results</li>
                  <li>• Detection timeline</li>
                  <li>• Log correlation findings</li>
                </ul>
              </div>
              
              {/* Remediation Report */}
              <div 
                className={`rounded-xl p-5 border cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-slate-900/50 border-slate-800 hover:border-green-500/50' : 'bg-slate-50 border-slate-200 hover:border-green-500'}`}
                onClick={() => generateComprehensiveReport('remediation')}
                data-testid="bas-remediation-report-btn"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-8 h-8 text-green-500" />
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation Guide</h4>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Actionable improvement steps</p>
                  </div>
                </div>
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <li>• Prioritized recommendations</li>
                  <li>• Detection rule suggestions</li>
                  <li>• Tool configuration tips</li>
                  <li>• Security control mapping</li>
                </ul>
              </div>
            </div>
            
            {/* Recent Simulations for Report Generation */}
            <div className={`border-t pt-6 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
              <h4 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Select Simulation for Report</h4>
              {simulations.length > 0 ? (
                <div className="space-y-3">
                  {simulations.slice(0, 5).map((sim, idx) => (
                    <div
                      key={sim.id || idx}
                      className={`flex items-center justify-between p-4 rounded-lg border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                          <Target className={`w-5 h-5 ${sim.status === 'completed' ? 'text-green-500' : 'text-orange-500'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                            {sim.name || sim.campaign_name || `Simulation ${idx + 1}`}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {sim.techniques_detected || 0}/{sim.techniques_total || 0} techniques detected • {new Date(sim.started_at || sim.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          (sim.techniques_detected / (sim.techniques_total || 1)) >= 0.7 ? 'text-green-500' :
                          (sim.techniques_detected / (sim.techniques_total || 1)) >= 0.4 ? 'text-yellow-500' : 'text-red-500'
                        }`}>
                          {sim.techniques_total > 0 ? Math.round((sim.techniques_detected / sim.techniques_total) * 100) : 0}%
                        </span>
                        <Button
                          size="sm"
                          onClick={() => {
                            toast.info('Generating full report for this simulation...');
                            setTimeout(() => toast.success('Report generated successfully!'), 2000);
                          }}
                          className="bg-red-600 hover:bg-red-500"
                          data-testid={`generate-report-sim-${idx}`}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Generate
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Target className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} />
                  <p className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No Simulations Yet</p>
                  <p className={`text-sm mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Run a simulation to generate detailed reports
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* AI Campaign Generator Modal */}
      {showAIGenerator && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 ${isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" />
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  AI Campaign Generator
                </h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAIGenerator(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Describe the attack scenario you want to simulate and AI will generate a comprehensive campaign with MITRE ATT&CK mapping.
            </p>
            
            <Textarea
              placeholder="Example: Simulate an APT group targeting financial institutions using spear-phishing for initial access, followed by credential harvesting and lateral movement to reach domain controllers..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className={`min-h-[120px] mb-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
            />
            
            <div className="flex items-center gap-2 mb-4">
              <Info className={`w-4 h-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                AI will automatically map techniques to MITRE ATT&CK framework
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-slate-700' : ''}`}
                onClick={() => setShowAIGenerator(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600"
                onClick={generateAICampaign}
                disabled={generatingCampaign}
              >
                {generatingCampaign ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Campaign
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Custom Campaign Modal (from selected techniques) */}
      {showCreateCustom && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 ${isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-red-500" />
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Create Custom Campaign
                </h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowCreateCustom(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Create a campaign from your {selectedTechniques.length} selected techniques
            </p>
            
            <div className="space-y-4">
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Campaign Name *
                </label>
                <Input 
                  placeholder="e.g., Custom Credential Theft Test"
                  value={customCampaignName}
                  onChange={(e) => setCustomCampaignName(e.target.value)}
                  className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                  data-testid="custom-campaign-name-input"
                />
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description
                </label>
                <Textarea
                  placeholder="Describe the purpose of this campaign..."
                  value={customCampaignDesc}
                  onChange={(e) => setCustomCampaignDesc(e.target.value)}
                  className={`mt-1 min-h-[80px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                  data-testid="custom-campaign-desc-input"
                />
              </div>
              
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Selected Techniques ({selectedTechniques.length})
                </p>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {selectedTechniques.map(techId => (
                    <Badge key={techId} variant="outline" className="text-[10px]">
                      {techId}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-slate-700' : ''}`}
                onClick={() => setShowCreateCustom(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={createCustomCampaign}
                data-testid="create-custom-campaign-submit"
              >
                <Target className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Configuration Modal */}
      {showCampaignConfig && configCampaign && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 ${isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-blue-500" />
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Configure Campaign
                </h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowCampaignConfig(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Configure settings for: <span className="font-medium text-white">{configCampaign.name}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Target URL
                </label>
                <Input 
                  placeholder="https://target.example.com"
                  value={campaignConfig.target}
                  onChange={(e) => setCampaignConfig(prev => ({ ...prev, target: e.target.value }))}
                  className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                  data-testid="campaign-config-target"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Stealth Mode
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Execute techniques with evasion
                  </p>
                </div>
                <Switch 
                  checked={campaignConfig.stealthMode}
                  onCheckedChange={(checked) => setCampaignConfig(prev => ({ ...prev, stealthMode: checked }))}
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Max Concurrent Techniques
                  </p>
                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {campaignConfig.maxConcurrentTechniques}
                  </span>
                </div>
                <Slider
                  value={[campaignConfig.maxConcurrentTechniques]}
                  onValueChange={(val) => setCampaignConfig(prev => ({ ...prev, maxConcurrentTechniques: val[0] }))}
                  max={10}
                  min={1}
                  step={1}
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Delay Between Techniques (seconds)
                  </p>
                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {campaignConfig.delayBetweenTechniques}s
                  </span>
                </div>
                <Slider
                  value={[campaignConfig.delayBetweenTechniques]}
                  onValueChange={(val) => setCampaignConfig(prev => ({ ...prev, delayBetweenTechniques: val[0] }))}
                  max={30}
                  min={0}
                  step={1}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Notify on Completion
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Send notification when finished
                  </p>
                </div>
                <Switch 
                  checked={campaignConfig.notifyOnCompletion}
                  onCheckedChange={(checked) => setCampaignConfig(prev => ({ ...prev, notifyOnCompletion: checked }))}
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-slate-700' : ''}`}
                onClick={saveCampaignConfig}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Config
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={runConfiguredCampaign}
                data-testid="run-configured-campaign"
              >
                <Play className="w-4 h-4 mr-2" />
                Save & Run
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Configuration Modal */}
      {showScheduleConfig && configSchedule && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 ${isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-500" />
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Configure Schedule
                </h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowScheduleConfig(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Configure schedule for: <span className="font-medium text-white">{configSchedule.campaign_name}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Schedule Type
                </label>
                <Select 
                  value={scheduleConfig.scheduleType} 
                  onValueChange={(val) => setScheduleConfig(prev => ({ ...prev, scheduleType: val }))}
                >
                  <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="custom">Custom Cron</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {scheduleConfig.scheduleType === 'custom' && (
                <div>
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Cron Expression
                  </label>
                  <Input 
                    placeholder="0 2 * * 0"
                    value={scheduleConfig.cronExpression}
                    onChange={(e) => setScheduleConfig(prev => ({ ...prev, cronExpression: e.target.value }))}
                    className={`mt-1 font-mono ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                  />
                </div>
              )}
              
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Auto-Compare & Alerts
                </h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      Auto-compare with previous run
                    </p>
                    <Switch 
                      checked={scheduleConfig.autoCompare}
                      onCheckedChange={(checked) => setScheduleConfig(prev => ({ ...prev, autoCompare: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      Alert when detection drops
                    </p>
                    <Switch 
                      checked={scheduleConfig.notifyOnDrop}
                      onCheckedChange={(checked) => setScheduleConfig(prev => ({ ...prev, notifyOnDrop: checked }))}
                    />
                  </div>
                  
                  {scheduleConfig.notifyOnDrop && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                          Alert Threshold
                        </p>
                        <span className={`text-sm font-medium ${scheduleConfig.alertThreshold >= 70 ? 'text-green-500' : 'text-red-500'}`}>
                          {scheduleConfig.alertThreshold}%
                        </span>
                      </div>
                      <Slider
                        value={[scheduleConfig.alertThreshold]}
                        onValueChange={(val) => setScheduleConfig(prev => ({ ...prev, alertThreshold: val[0] }))}
                        max={100}
                        min={0}
                        step={5}
                      />
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Alert when detection rate falls below this threshold
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Notification Email (optional)
                </label>
                <Input 
                  placeholder="alerts@company.com"
                  value={scheduleConfig.notificationEmail}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, notificationEmail: e.target.value }))}
                  className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-slate-700' : ''}`}
                onClick={() => setShowScheduleConfig(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={saveScheduleConfig}
                data-testid="save-schedule-config"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Schedule
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Technique Modal */}
      {showCustomTechnique && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-lg rounded-xl p-6 ${isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-violet-500" />
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Add Custom Technique
                </h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowCustomTechnique(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Technique ID *
                  </label>
                  <Input 
                    placeholder="T9001"
                    value={newTechnique.id}
                    onChange={(e) => setNewTechnique(prev => ({ ...prev, id: e.target.value }))}
                    className={`mt-1 font-mono ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                    data-testid="custom-technique-id"
                  />
                </div>
                <div>
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Severity
                  </label>
                  <Select 
                    value={newTechnique.severity} 
                    onValueChange={(val) => setNewTechnique(prev => ({ ...prev, severity: val }))}
                  >
                    <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Technique Name *
                </label>
                <Input 
                  placeholder="e.g., Custom Credential Exfiltration"
                  value={newTechnique.name}
                  onChange={(e) => setNewTechnique(prev => ({ ...prev, name: e.target.value }))}
                  className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                  data-testid="custom-technique-name"
                />
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Tactic
                </label>
                <Select 
                  value={newTechnique.tactic} 
                  onValueChange={(val) => setNewTechnique(prev => ({ ...prev, tactic: val }))}
                >
                  <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MITRE_TACTICS.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description
                </label>
                <Textarea
                  placeholder="Describe the technique and how it works..."
                  value={newTechnique.description}
                  onChange={(e) => setNewTechnique(prev => ({ ...prev, description: e.target.value }))}
                  className={`mt-1 min-h-[80px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                />
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Detection Methods
                </label>
                <Textarea
                  placeholder="How can this technique be detected?"
                  value={newTechnique.detection_methods}
                  onChange={(e) => setNewTechnique(prev => ({ ...prev, detection_methods: e.target.value }))}
                  className={`mt-1 min-h-[60px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                />
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Mitigations
                </label>
                <Textarea
                  placeholder="How can this technique be mitigated?"
                  value={newTechnique.mitigations}
                  onChange={(e) => setNewTechnique(prev => ({ ...prev, mitigations: e.target.value }))}
                  className={`mt-1 min-h-[60px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-slate-700' : ''}`}
                onClick={() => setShowCustomTechnique(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-violet-600 hover:bg-violet-700"
                onClick={saveCustomTechnique}
                data-testid="save-custom-technique"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Technique
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Simulation Results Modal with Recommendations */}
      {showSimulationResults && selectedSimulation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl p-6 ${isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-500" />
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Simulation Results & Recommendations
                </h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowSimulationResults(false)}
              >
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {selectedSimulation.campaign_name}
              </h4>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Target: {selectedSimulation.target || 'N/A'} • 
                Completed: {selectedSimulation.completed_at ? new Date(selectedSimulation.completed_at).toLocaleString() : 'N/A'}
              </p>
            </div>
            
            {/* Results Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {selectedSimulation.techniques_total || 10}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total Techniques</p>
              </div>
              <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
                <p className="text-3xl font-bold text-green-500">
                  {((selectedSimulation.techniques_detected || 0) / Math.max(selectedSimulation.techniques_total, 1) * 100).toFixed(0)}%
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Detection Rate</p>
              </div>
              <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                <p className="text-3xl font-bold text-blue-500">
                  {((selectedSimulation.techniques_blocked || 0) / Math.max(selectedSimulation.techniques_total, 1) * 100).toFixed(0)}%
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Block Rate</p>
              </div>
              <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-3xl font-bold text-red-500">
                  {(selectedSimulation.techniques_total || 10) - (selectedSimulation.techniques_detected || 0)}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Evaded Detection</p>
              </div>
            </div>
            
            {/* AI-Powered Deep Analysis Section */}
            <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-gradient-to-r from-violet-500/10 to-blue-500/10 border-violet-500/30' : 'bg-gradient-to-r from-violet-50 to-blue-50 border-violet-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    AI-Powered Deep Analysis
                  </h4>
                  <Badge className="bg-violet-500/20 text-violet-500 text-[10px]">Claude Sonnet</Badge>
                </div>
                {!aiAnalysis && (
                  <Button
                    size="sm"
                    onClick={() => fetchAiAnalysis(selectedSimulation.id)}
                    disabled={loadingAiAnalysis}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="generate-ai-analysis-btn"
                  >
                    {loadingAiAnalysis ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        Generate AI Analysis
                      </>
                    )}
                  </Button>
                )}
              </div>

              {aiAnalysis ? (
                <div className="space-y-4">
                  {/* Executive Summary */}
                  {aiAnalysis.ai_analysis?.executive_summary && (
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                      <h5 className={`text-sm font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <FileText className="w-4 h-4 text-blue-500" />
                        Executive Summary
                      </h5>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {aiAnalysis.ai_analysis.executive_summary}
                      </p>
                    </div>
                  )}

                  {/* Critical Gaps */}
                  {aiAnalysis.ai_analysis?.critical_gaps && aiAnalysis.ai_analysis.critical_gaps.length > 0 && (
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                      <h5 className={`text-sm font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        Critical Gaps Identified
                      </h5>
                      <ul className="space-y-1">
                        {aiAnalysis.ai_analysis.critical_gaps.map((gap, idx) => (
                          <li key={idx} className={`text-sm flex items-start gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span className="text-red-500 mt-1">•</span>
                            <span>{typeof gap === 'object' ? gap.description || gap.gap || JSON.stringify(gap) : gap}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Immediate Actions */}
                  {aiAnalysis.ai_analysis?.immediate_actions && aiAnalysis.ai_analysis.immediate_actions.length > 0 && (
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                      <h5 className={`text-sm font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <Zap className="w-4 h-4 text-orange-500" />
                        Immediate Actions Required
                      </h5>
                      <ul className="space-y-1">
                        {aiAnalysis.ai_analysis.immediate_actions.map((action, idx) => (
                          <li key={idx} className={`text-sm flex items-start gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span className="text-orange-500 font-bold">{idx + 1}.</span>
                            <span>{typeof action === 'object' ? action.action || action.description || JSON.stringify(action) : action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* AI SIEM Rules */}
                  {aiAnalysis.ai_analysis?.siem_rules && aiAnalysis.ai_analysis.siem_rules.length > 0 && (
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                      <h5 className={`text-sm font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <Shield className="w-4 h-4 text-green-500" />
                        AI-Recommended SIEM Rules
                      </h5>
                      <div className="space-y-2">
                        {aiAnalysis.ai_analysis.siem_rules.map((rule, idx) => (
                          <div key={idx} className={`p-2 rounded font-mono text-xs ${isDarkMode ? 'bg-slate-900 text-green-400' : 'bg-slate-100 text-green-700'}`}>
                            {typeof rule === 'object' ? (rule.rule || rule.query || JSON.stringify(rule)) : rule}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 30-Day Improvement Plan */}
                  {aiAnalysis.ai_analysis?.improvement_plan && (
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                      <h5 className={`text-sm font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        <Calendar className="w-4 h-4 text-blue-500" />
                        30-Day Improvement Plan
                      </h5>
                      <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {typeof aiAnalysis.ai_analysis.improvement_plan === 'string' 
                          ? aiAnalysis.ai_analysis.improvement_plan 
                          : Array.isArray(aiAnalysis.ai_analysis.improvement_plan) 
                            ? aiAnalysis.ai_analysis.improvement_plan.map((item, idx) => (
                                <p key={idx} className="mb-1">
                                  <span className="font-medium">Week {idx + 1}:</span> {typeof item === 'object' ? item.action || item.description || JSON.stringify(item) : item}
                                </p>
                              ))
                            : JSON.stringify(aiAnalysis.ai_analysis.improvement_plan)
                        }
                      </div>
                    </div>
                  )}

                  {/* Raw response fallback */}
                  {aiAnalysis.ai_analysis?.raw_response && !aiAnalysis.ai_analysis.executive_summary && (
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                      <h5 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        AI Analysis
                      </h5>
                      <p className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {aiAnalysis.ai_analysis.raw_response}
                      </p>
                    </div>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAiAnalysis(null)}
                    className={`w-full ${isDarkMode ? 'border-slate-700' : ''}`}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate Analysis
                  </Button>
                </div>
              ) : (
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Click "Generate AI Analysis" to get AI-powered deep insights, critical gaps identification, SIEM rules, and a 30-day improvement roadmap based on your simulation results.
                </p>
              )}
            </div>

            {/* Recommendations Section */}
            <div className="mb-6">
              <h4 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Standard Recommendations
              </h4>
              
              <div className="space-y-3">
                {generateRecommendations(selectedSimulation).map((rec, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border-l-4 ${
                      rec.severity === 'critical' ? 'border-red-500 bg-red-500/5' :
                      rec.severity === 'high' ? 'border-orange-500 bg-orange-500/5' :
                      rec.severity === 'medium' ? 'border-yellow-500 bg-yellow-500/5' :
                      rec.severity === 'info' ? 'border-blue-500 bg-blue-500/5' :
                      'border-slate-500 bg-slate-500/5'
                    } ${isDarkMode ? '' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[10px] ${
                        rec.severity === 'critical' ? 'bg-red-500' :
                        rec.severity === 'high' ? 'bg-orange-500' :
                        rec.severity === 'medium' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      } text-white`}>
                        {rec.severity.toUpperCase()}
                      </Badge>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {rec.title}
                      </span>
                    </div>
                    <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {rec.description}
                    </p>
                    <div className={`flex items-center gap-2 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      <ChevronRight className="w-3 h-3" />
                      <span>{rec.action}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Suggested SIEM Rules */}
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Suggested Detection Rules
              </h4>
              <div className="space-y-2">
                <div className={`p-2 rounded font-mono text-xs ${isDarkMode ? 'bg-slate-900 text-green-400' : 'bg-white text-green-700'}`}>
                  # SIEM Rule: Detect lateral movement attempts<br/>
                  index=security sourcetype=windows:security EventCode=4624 Logon_Type=3
                </div>
                <div className={`p-2 rounded font-mono text-xs ${isDarkMode ? 'bg-slate-900 text-green-400' : 'bg-white text-green-700'}`}>
                  # EDR Policy: Block credential dumping<br/>
                  process.name:("mimikatz.exe" OR "procdump.exe") AND process.args:("lsass" OR "sekurlsa")
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-slate-700' : ''}`}
                onClick={() => setShowSimulationResults(false)}
              >
                Close
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={() => exportSimulationPDF(selectedSimulation)}
                data-testid="export-simulation-pdf"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
